'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { readLocationCookie } from '@/lib/location';
import { anchorForTypeParam } from '@/lib/posts/sections';
import { ARRIVAL_STORAGE_KEY } from '@/lib/posts/pinSection';
import LocationModal from '@/components/common/LocationModal';
import { apiGet } from '@/lib/api';
import { Loader2 } from 'lucide-react';

/**
 * /jobs-hub links here as /posts?type=hiring|looking|connect|services, and
 * the metro page groups its categories into #type-* sections. Turn the CTA
 * bucket into that section id so the redirect can carry it. Returns '' when
 * there is no bucket, which is the ordinary /posts visit.
 *
 * Read off window rather than useSearchParams: every caller here runs after
 * mount, and useSearchParams would add a Suspense requirement for a value
 * nothing needs at render time.
 *
 * This handoff used to live in src/app/posts/ClientPage.tsx. It never ran:
 * the bucket landing was written into that file on 2026-08-10, six weeks
 * after /posts/page.tsx had stopped rendering it, so all four jobs-hub CTAs
 * landed on the undifferentiated metro page. It lives here now because this
 * is what /posts actually renders.
 */
function requestedAnchor(): string {
  if (typeof window === 'undefined') return '';
  try {
    return anchorForTypeParam(new URLSearchParams(window.location.search).get('type')) || '';
  } catch {
    return '';
  }
}

/**
 * Relay the bucket through sessionStorage as well as the hash. The no-cookie
 * path hands off to the Header's global LocationModal, which navigates to
 * /posts/{metro} WITHOUT a hash, so the hash alone would drop the intent for
 * exactly the first-time cohort the jobs-hub CTAs are aimed at. The metro
 * page reads hash-or-relay, opens the category fold and lands the section.
 * The key is imported from the reader so the two sides cannot drift, and the
 * reader accepts only the four ids the section cards actually carry.
 */
function relayAnchor(anchor: string): void {
  if (!anchor) return;
  try { sessionStorage.setItem(ARRIVAL_STORAGE_KEY, anchor); } catch { /* private mode */ }
}

export default function PostsLanding() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const [checking, setChecking] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);

  useEffect(() => {
    async function checkLocation() {
      // Two shapes of the same thing: the relay stores the bare section id,
      // because the metro page tests it with startsWith('type-'), and the URL
      // needs it as a fragment.
      const anchor = requestedAnchor();
      const hash = anchor ? `#${anchor}` : '';
      relayAnchor(anchor);
      try {
        // 1. If logged in, check profile for location
        if (status === 'authenticated' && session) {
          try {
            const profile: any = await apiGet('/api/profile', session);
            // Check if user has a location set in profile
            if (profile?.location || profile?.Location) {
              // TODO: Need to map location ID to metro slug
              // For now, fall through to cookie check
            }
          } catch (e) {
            console.warn('Failed to load profile location:', e);
          }
        }

        // 2. Check cookie for location
        const cookieLoc = readLocationCookie();
        if (cookieLoc?.metro) {
          // replace, not push: /posts is an interstitial that redirects on
          // sight, so leaving it in history makes Back bounce the visitor
          // straight forward again.
          router.replace(`/posts/${cookieLoc.metro}${hash}`);
          return;
        }

        // 3. No location found, show modal
        setShowLocationModal(true);
      } catch (e) {
        console.error('Location check failed:', e);
        setShowLocationModal(true);
      } finally {
        setChecking(false);
      }
    }

    if (status !== 'loading') {
      checkLocation();
    }
  }, [status, session, router]);

  const handleLocationChanged = (state: string, metroSlug: string) => {
    setShowLocationModal(false);
    // Still on /posts here, so the ?type= is intact and a visitor who came
    // from a jobs-hub CTA lands on their section after picking a metro.
    const anchor = requestedAnchor();
    router.replace(`/posts/${metroSlug}${anchor ? `#${anchor}` : ''}`);
  };

  if (checking || status === 'loading') {
    return (
      <div className="container mx-auto py-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto py-20 text-center">
        {/* h2, not h1: this is the client-only location-picker interstitial
            shown before redirect. The page's crawlable h1 is the server-rendered
            "Browse porn & adult jobs by city" heading in posts/page.tsx; keeping
            this as h1 would create a duplicate h1 after hydration. */}
        <h2 className="text-3xl font-bold mb-4">Porn &amp; Adult Jobs by City</h2>
        <p className="text-muted-foreground mb-8">
          Select your location to browse casting calls, gigs, and classifieds near you.
        </p>
      </div>

      <LocationModal
        open={showLocationModal}
        onClose={() => router.push('/')}
        onChanged={handleLocationChanged}
      />
    </>
  );
}
